import { useLanguage } from "@/contexts/LanguageContext";
import translations from "@/constants/translations";
import { MapPin, Phone, Mail, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
};

export default function Footer({ settings }) {
  const { lang } = useLanguage();
  const t = translations[lang];
  const year = new Date().getFullYear();

  const logoSrc = settings?.logo_footer ? getImageUrl(settings.logo_footer) : "";
  const reserveUrl = settings?.reservation_url || "#";

  return (
    <footer className="bg-[#111827] text-gray-300">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="O Quinto Império"
                className="h-20 w-auto object-contain mb-4 brightness-0 invert"
              />
            ) : (
              <span className="font-cormorant text-2xl text-white block mb-4">
                O Quinto Império
              </span>
            )}
            <p className="font-lato text-sm text-gray-400 leading-relaxed">
              {lang === "pt"
                ? "Cozinha mediterrânica no coração do Algarve."
                : "Mediterranean cuisine in the heart of the Algarve."}
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-lato text-xs tracking-widest uppercase text-white mb-4">
              {t.footer.links}
            </h4>
            <ul className="space-y-2">
              <li>
                <a href="/" className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200">
                  {t.nav.home}
                </a>
              </li>
              <li>
                <a href="/menu" className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200">
                  {t.nav.menu}
                </a>
              </li>
              <li>
                <a href="/#location" className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200">
                  {t.nav.location}
                </a>
              </li>
              <li>
                <a
                  href={reserveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200"
                >
                  {t.nav.reserve}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-lato text-xs tracking-widest uppercase text-white mb-4">
              {t.footer.contact}
            </h4>
            <ul className="space-y-3">
              {settings?.address && (
                <li className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#0077B6] mt-0.5 flex-shrink-0" />
                  <span className="font-lato text-sm text-gray-400">{settings.address}</span>
                </li>
              )}
              {settings?.phone && (
                <li className="flex items-center gap-2">
                  <Phone size={14} className="text-[#0077B6] flex-shrink-0" />
                  <a
                    href={`tel:${settings.phone}`}
                    className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200"
                  >
                    {settings.phone}
                  </a>
                </li>
              )}
              {settings?.email && (
                <li className="flex items-center gap-2">
                  <Mail size={14} className="text-[#0077B6] flex-shrink-0" />
                  <a
                    href={`mailto:${settings.email}`}
                    className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200"
                  >
                    {settings.email}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Social & Reserve */}
          <div>
            <h4 className="font-lato text-xs tracking-widest uppercase text-white mb-4">
              {t.footer.social}
            </h4>
            <div className="flex flex-col gap-2 mb-6">
              {settings?.social_facebook && (
                <a
                  href={settings.social_facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200 flex items-center gap-1"
                >
                  Facebook <ExternalLink size={12} />
                </a>
              )}
              {settings?.social_instagram && (
                <a
                  href={settings.social_instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200 flex items-center gap-1"
                >
                  Instagram <ExternalLink size={12} />
                </a>
              )}
              {settings?.social_tripadvisor && (
                <a
                  href={settings.social_tripadvisor}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-lato text-sm text-gray-400 hover:text-[#0077B6] transition-colors duration-200 flex items-center gap-1"
                >
                  TripAdvisor <ExternalLink size={12} />
                </a>
              )}
            </div>

            <a
              href={reserveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0077B6] text-white font-lato text-xs tracking-widest uppercase hover:bg-[#005f8f] transition-colors duration-200"
            >
              {t.footer.reserve}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-700/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="font-lato text-xs text-gray-500">
            &copy; {year} O Quinto Império. {t.footer.rights}.
          </p>
          <a
            href="/admin"
            className="font-lato text-xs text-gray-600 hover:text-gray-400 transition-colors duration-200"
          >
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
}
